import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Copy, Loader2, Pencil } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { request } from "../lib/api";
import type { CreatedResponse, Notice, Question, QuestionType } from "../types";
import { ASSESSMENT_ROLE_LABELS, QUESTION_TYPES, QUESTION_TYPE_LABELS } from "../types";
import { AttributionCell } from "../components/Attribution";
import { DragList } from "../components/DragList";
import { useAttribution } from "../lib/useAttribution";
import {
  Card,
  ConfirmModal,
  InlineNotice,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  textareaClass,
} from "../components/ui";

interface QuestionsResponse {
  content: { questions: Question[]; version?: number; assessment_role?: string | null };
  translations?: Record<string, { title?: string | null; strings?: Record<string, string> | null }>;
}

const LANGUAGE = "ur";

// The quiz item's Urdu wording: its title plus a dictionary from each English
// string to its translation, the shape the اردو page saves.
interface UrduTranslation {
  title: string;
  strings: Record<string, string>;
}

// Older saves stored the dictionary keys trimmed.
const urduFor = (strings: Record<string, string>, english: string) =>
  strings[english] ?? strings[english.trim()] ?? "";

const reasonOf = (q: Question) => q.explanation || q.feedback_incorrect || "";

// Each English text the editor shows, paired with the Urdu typed beside it.
function urduPairs(q: Question): [string, string][] {
  return [
    [q.text, q.urdu ?? ""],
    [reasonOf(q), q.explanation_urdu ?? ""],
    ...q.options.map((o): [string, string] => [o.text, o.urdu ?? ""]),
  ];
}

function UrduField({ value, onChange, multiline = false }: { value: string; onChange: (value: string) => void; multiline?: boolean }) {
  const props = {
    dir: "rtl" as const,
    lang: LANGUAGE,
    value,
    placeholder: "اردو ترجمہ (اختیاری)",
    "aria-label": "Urdu translation",
  };
  return multiline ? (
    <textarea className={textareaClass} rows={2} {...props} onChange={(e) => onChange(e.target.value)} />
  ) : (
    <input className={inputClass} {...props} onChange={(e) => onChange(e.target.value)} />
  );
}

function emptyQuestion(position: number): Question {
  return {
    type: "single_choice",
    text: "",
    position,
    points: 1,
    required: true,
    settings: {},
    options: [
      { text: "", is_correct: true, position: 1 },
      { text: "", is_correct: false, position: 2 },
    ],
    match_pairs: [],
    order_items: [],
    blanks: [],
    children: [],
  };
}

export function QuizBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const attribution = useAttribution(
    "questions",
    questions.flatMap((question) => (question.id ? [question.id] : []))
  );
  const [urdu, setUrdu] = useState<UrduTranslation>({ title: "", strings: {} });
  const [edition, setEdition] = useState<{ version: number; role: string | null } | null>(null);
  const [editing, setEditing] = useState<Question | null>(null);
  const [message, setMessage] = useState<Notice | null>(null);
  const [confirmDeleteID, setConfirmDeleteID] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    request<QuestionsResponse>(`/api/admin/content-items/${id}`)
      .then((data) => {
        setQuestions((data.content.questions || []).sort((a, b) => a.position - b.position));
        setEdition(
          typeof data.content.version === "number"
            ? { version: data.content.version, role: data.content.assessment_role ?? null }
            : null
        );
        const saved = data.translations?.[LANGUAGE];
        setUrdu({ title: saved?.title ?? "", strings: saved?.strings ?? {} });
      })
      .catch((cause) => setMessage({ tone: "error", text: errorMessage(cause) }));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function reorder(next: Question[]) {
    setQuestions(next);
    try {
      await request("/api/admin/questions/reorder", {
        method: "POST",
        body: JSON.stringify({ quiz_id: id, question_ids: next.map((q) => q.id) }),
      });
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
      load();
    }
  }

  async function duplicate(questionID?: string) {
    if (!questionID) return;
    try {
      await request(`/api/admin/questions/${questionID}/duplicate`, { method: "POST" });
      load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    }
  }

  async function remove(questionID: string) {
    try {
      await request(`/api/admin/questions/${questionID}`, { method: "DELETE" });
      setConfirmDeleteID(null);
      load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
      {/* Back to wherever this was opened from: the PD day builder or the content library. */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-interactive hover:underline xl:col-span-2"
      >
        <ArrowLeft size={16} /> Back
      </button>
      <Card title="Quiz questions" text="Add, edit, duplicate, and reorder questions.">
        {message && <InlineNotice tone={message.tone} text={message.text} />}
        {edition && (
          <p className="mb-3 text-xs text-content-secondary">
            Edition {edition.version} ·{" "}
            {edition.role ? ASSESSMENT_ROLE_LABELS[edition.role] : "Not tagged as pre- or post-test"}
            . Editing a question starts a new edition; results are kept per edition.
          </p>
        )}
        {questions.length === 0 ? (
          <button type="button" className={secondaryButtonClass} onClick={() => setEditing(emptyQuestion(1))}>
            + Add question
          </button>
        ) : (
          <DragList
            items={questions}
            onChange={reorder}
            onRemove={(i) => setConfirmDeleteID(questions[i].id || null)}
            addLabel="Add question"
            onAdd={() => setEditing(emptyQuestion(questions.length + 1))}
            renderItem={(q) => (
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-content-primary">{q.text || "(untitled question)"}</p>
                  <p className="text-xs text-content-secondary">
                    {QUESTION_TYPE_LABELS[q.type]}
                    {q.type !== "section" && ` · ${q.points} pt${q.points === 1 ? "" : "s"}`}
                  </p>
                  {q.id && (
                    <div className="mt-1">
                      <AttributionCell info={attribution[q.id]} />
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button type="button" className="text-content-secondary hover:text-interactive" onClick={() => setEditing(q)} aria-label="Edit">
                    <Pencil size={15} />
                  </button>
                  <button type="button" className="text-content-secondary hover:text-interactive" onClick={() => duplicate(q.id)} aria-label="Duplicate">
                    <Copy size={15} />
                  </button>
                </div>
              </div>
            )}
          />
        )}
      </Card>
      {editing && id && (
        <QuestionEditor
          // The editor copies the question into its own state, so it must
          // remount when a different question is picked.
          key={editing.id ?? `new-${editing.position}`}
          quizID={id}
          question={editing}
          urdu={urdu}
          onSaved={(strings) => {
            // Kept at once: the next question opened must not start from the
            // old dictionary and save over this one's Urdu.
            setUrdu((current) => ({ ...current, strings }));
            setEditing(null);
            load();
          }}
          onCancel={() => setEditing(null)}
        />
      )}
      <ConfirmModal
        open={Boolean(confirmDeleteID)}
        title="Delete question?"
        message="This removes the question and any learner responses to it."
        onConfirm={() => confirmDeleteID && remove(confirmDeleteID)}
        onCancel={() => setConfirmDeleteID(null)}
      />
    </div>
  );
}

function QuestionEditor({
  quizID,
  question,
  urdu,
  onSaved,
  onCancel,
}: {
  quizID: string;
  question: Question;
  urdu: UrduTranslation;
  onSaved: (strings: Record<string, string>) => void;
  onCancel: () => void;
}) {
  // Urdu is edited beside the English it translates, so it rides on the
  // question and its options until saved.
  const [q, setQ] = useState<Question>(() => ({
    ...question,
    urdu: urduFor(urdu.strings, question.text),
    explanation_urdu: urduFor(urdu.strings, reasonOf(question)),
    options: question.options.map((o) => ({ ...o, urdu: urduFor(urdu.strings, o.text) })),
  }));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Notice | null>(null);
  const isSection = q.type === "section";

  function patch(next: Partial<Question>) {
    setQ((prev) => ({ ...prev, ...next }));
  }

  function changeType(type: QuestionType) {
    const base = emptyQuestion(q.position);
    // A heading is not answered, so it carries no options, marks or reason.
    const heading = type === "section" ? { points: 0, required: false, explanation: null, options: [] } : {};
    const points = q.type === "section" ? { points: base.points, required: base.required } : {};
    patch({ type, options: base.options, match_pairs: [], order_items: [], blanks: [], children: [], settings: {}, ...points, ...heading });
  }

  async function submit() {
    setBusy(true);
    setMessage(null);
    // The question API rejects fields it does not know, so the Urdu is
    // left out here (undefined is dropped from the JSON) and saved to the
    // item's translation instead.
    const payload = {
      ...q,
      urdu: undefined,
      explanation_urdu: undefined,
      quiz_id: quizID,
      options: q.options.map((option) => ({ ...option, urdu: undefined })),
    };
    const strings = { ...urdu.strings };
    for (const [source, translated] of urduPairs(q)) {
      if (!source.trim()) continue;
      if (translated.trim()) strings[source] = translated.trim();
      else {
        delete strings[source];
        delete strings[source.trim()];
      }
    }
    try {
      if (q.id) {
        await request(`/api/admin/questions/${q.id}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        await request<CreatedResponse>("/api/admin/questions", { method: "POST", body: JSON.stringify(payload) });
      }
      if (JSON.stringify(strings) !== JSON.stringify(urdu.strings)) {
        await request(`/api/admin/content-items/${quizID}/translations/${LANGUAGE}`, {
          method: "PUT",
          body: JSON.stringify({ title: urdu.title, strings }),
        });
      }
      onSaved(strings);
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={q.id ? "Edit question" : "New question"}
      text={isSection ? "A heading shown above the questions that follow it. It is not answered or scored." : "Visual builder — no JSON required."}
    >
      <div className="grid gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className={labelClass}>
            Question type
            <select className={inputClass} value={q.type} onChange={(e) => changeType(e.target.value as QuestionType)}>
              {QUESTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {QUESTION_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          {!isSection && (
            <label className={labelClass}>
              Points
              <input type="number" min={0} className={inputClass} value={q.points} onChange={(e) => patch({ points: Number(e.target.value) })} />
            </label>
          )}
        </div>
        <label className={labelClass}>
          {isSection ? "Heading text" : `Question text ${q.type === "scenario" ? "(scenario)" : ""}`}
          <textarea className={textareaClass} value={q.text} onChange={(e) => patch({ text: e.target.value })} />
          <UrduField multiline value={q.urdu ?? ""} onChange={(value) => patch({ urdu: value })} />
        </label>
        {!isSection && (
          <>
            <label className="flex items-center gap-2 text-sm font-medium text-content-primary">
              <input type="checkbox" className="h-4 w-4 accent-interactive" checked={q.required} onChange={(e) => patch({ required: e.target.checked })} />
              Required
            </label>

            <QuestionTypeBody q={q} patch={patch} />

            <label className={labelClass}>
              Reason for the correct answer (shown to teachers who answer incorrectly)
              <textarea className={textareaClass} value={reasonOf(q)} onChange={(e) => patch({ explanation: e.target.value })} />
              <UrduField multiline value={q.explanation_urdu ?? ""} onChange={(value) => patch({ explanation_urdu: value })} />
            </label>
          </>
        )}

        {message && <InlineNotice tone={message.tone} text={message.text} />}
        <div className="flex items-center gap-3">
          <button type="button" className={primaryButtonClass} disabled={busy} onClick={submit}>
            {busy && <Loader2 size={16} className="animate-spin" />}
            {isSection ? "Save heading" : "Save question"}
          </button>
          <button type="button" className={secondaryButtonClass} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </Card>
  );
}

function QuestionTypeBody({ q, patch }: { q: Question; patch: (next: Partial<Question>) => void }) {
  switch (q.type) {
    case "single_choice":
    case "true_false":
    case "image_based":
    case "multiple_choice":
      return <OptionsEditor q={q} patch={patch} multi={q.type === "multiple_choice"} />;
    case "number":
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className={labelClass}>
            Correct number
            <input
              type="number"
              className={inputClass}
              value={(q.settings.correct_number as number) ?? ""}
              onChange={(e) => patch({ settings: { ...q.settings, correct_number: Number(e.target.value) } })}
            />
          </label>
          <label className={labelClass}>
            Tolerance (±)
            <input
              type="number"
              min={0}
              className={inputClass}
              value={q.numeric_tolerance ?? 0}
              onChange={(e) => patch({ numeric_tolerance: Number(e.target.value) })}
            />
          </label>
        </div>
      );
    case "matching":
    case "drag_drop":
      return <MatchPairsEditor q={q} patch={patch} />;
    case "ordering":
      return <OrderItemsEditor q={q} patch={patch} />;
    case "fill_blank":
      return <BlanksEditor q={q} patch={patch} withOptions={false} />;
    case "dropdown_blank":
      return <BlanksEditor q={q} patch={patch} withOptions />;
    case "scenario":
      return <ScenarioEditor q={q} patch={patch} />;
    case "short_text":
    case "long_text":
      return <p className="text-xs text-content-secondary">Graded manually — appears in the grading queue after submission.</p>;
    default:
      return null;
  }
}

function OptionsEditor({ q, patch, multi }: { q: Question; patch: (next: Partial<Question>) => void; multi: boolean }) {
  const options = q.options;
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-content-primary">Options — mark the correct answer{multi ? "s" : ""}</p>
      <DragList
        items={options}
        onChange={(next) => patch({ options: next.map((o, i) => ({ ...o, position: i + 1 })) })}
        onRemove={(i) => patch({ options: options.filter((_, idx) => idx !== i) })}
        addLabel="Add option"
        onAdd={() => patch({ options: [...options, { text: "", is_correct: false, position: options.length + 1 }] })}
        renderItem={(opt, i) => (
          <div className="flex items-center gap-3">
            <input
              type={multi ? "checkbox" : "radio"}
              name={`correct-${q.position}`}
              className="h-4 w-4 accent-interactive"
              checked={opt.is_correct}
              onChange={(e) =>
                patch({
                  options: options.map((o, idx) =>
                    idx === i ? { ...o, is_correct: e.target.checked } : multi ? o : { ...o, is_correct: false }
                  ),
                })
              }
            />
            <div className="grid min-w-0 flex-1 gap-2">
              <input
                className={inputClass}
                value={opt.text}
                placeholder="Option text"
                onChange={(e) => patch({ options: options.map((o, idx) => (idx === i ? { ...o, text: e.target.value } : o)) })}
              />
              <UrduField
                value={opt.urdu ?? ""}
                onChange={(value) => patch({ options: options.map((o, idx) => (idx === i ? { ...o, urdu: value } : o)) })}
              />
            </div>
          </div>
        )}
      />
    </div>
  );
}

function MatchPairsEditor({ q, patch }: { q: Question; patch: (next: Partial<Question>) => void }) {
  const pairs = q.match_pairs;
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-content-primary">Pairs — left item matched to right item</p>
      <DragList
        items={pairs}
        onChange={(next) => patch({ match_pairs: next.map((p, i) => ({ ...p, position: i + 1 })) })}
        onRemove={(i) => patch({ match_pairs: pairs.filter((_, idx) => idx !== i) })}
        addLabel="Add pair"
        onAdd={() => patch({ match_pairs: [...pairs, { left_text: "", right_text: "", position: pairs.length + 1 }] })}
        renderItem={(pair, i) => (
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              className={inputClass}
              placeholder="Left item"
              value={pair.left_text}
              onChange={(e) => patch({ match_pairs: pairs.map((p, idx) => (idx === i ? { ...p, left_text: e.target.value } : p)) })}
            />
            <input
              className={inputClass}
              placeholder="Matches with…"
              value={pair.right_text}
              onChange={(e) => patch({ match_pairs: pairs.map((p, idx) => (idx === i ? { ...p, right_text: e.target.value } : p)) })}
            />
          </div>
        )}
      />
    </div>
  );
}

function OrderItemsEditor({ q, patch }: { q: Question; patch: (next: Partial<Question>) => void }) {
  const items = q.order_items;
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-content-primary">Items, listed in the correct order</p>
      <DragList
        items={items}
        onChange={(next) => patch({ order_items: next.map((it, i) => ({ ...it, correct_position: i + 1 })) })}
        onRemove={(i) => patch({ order_items: items.filter((_, idx) => idx !== i) })}
        addLabel="Add item"
        onAdd={() => patch({ order_items: [...items, { text: "", correct_position: items.length + 1 }] })}
        renderItem={(item, i) => (
          <input
            className={inputClass}
            value={item.text}
            onChange={(e) => patch({ order_items: items.map((it, idx) => (idx === i ? { ...it, text: e.target.value } : it)) })}
          />
        )}
      />
    </div>
  );
}

function BlanksEditor({ q, patch, withOptions }: { q: Question; patch: (next: Partial<Question>) => void; withOptions: boolean }) {
  const blanks = q.blanks;
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-content-primary">
        Blanks — use <code>[[blank]]</code> markers in the question text, one entry per marker in order
      </p>
      <DragList
        items={blanks}
        onChange={(next) => patch({ blanks: next.map((b, i) => ({ ...b, blank_index: i + 1 })) })}
        onRemove={(i) => patch({ blanks: blanks.filter((_, idx) => idx !== i) })}
        addLabel="Add blank"
        onAdd={() => patch({ blanks: [...blanks, { blank_index: blanks.length + 1, correct_text: "", options: withOptions ? [{ text: "", is_correct: true, position: 1 }] : [] }] })}
        renderItem={(blank, i) =>
          withOptions ? (
            <div className="grid gap-2">
              <p className="text-xs text-content-secondary">Blank {i + 1} options — mark the correct one</p>
              {blank.options.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`blank-${i}`}
                    className="h-4 w-4 accent-interactive"
                    checked={opt.is_correct}
                    onChange={() =>
                      patch({
                        blanks: blanks.map((b, bi) =>
                          bi === i ? { ...b, options: b.options.map((o, ooi) => ({ ...o, is_correct: ooi === oi })) } : b
                        ),
                      })
                    }
                  />
                  <input
                    className={inputClass}
                    value={opt.text}
                    onChange={(e) =>
                      patch({
                        blanks: blanks.map((b, bi) =>
                          bi === i ? { ...b, options: b.options.map((o, ooi) => (ooi === oi ? { ...o, text: e.target.value } : o)) } : b
                        ),
                      })
                    }
                  />
                </div>
              ))}
              <button
                type="button"
                className="w-fit text-xs font-semibold text-interactive hover:underline"
                onClick={() =>
                  patch({
                    blanks: blanks.map((b, bi) =>
                      bi === i ? { ...b, options: [...b.options, { text: "", is_correct: false, position: b.options.length + 1 }] } : b
                    ),
                  })
                }
              >
                + Add option
              </button>
            </div>
          ) : (
            <input
              className={inputClass}
              placeholder={`Correct text for blank ${i + 1}`}
              value={blank.correct_text}
              onChange={(e) => patch({ blanks: blanks.map((b, bi) => (bi === i ? { ...b, correct_text: e.target.value } : b)) })}
            />
          )
        }
      />
    </div>
  );
}

function ScenarioEditor({ q, patch }: { q: Question; patch: (next: Partial<Question>) => void }) {
  const children = q.children;
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-content-primary">Follow-up questions for this scenario</p>
      <DragList
        items={children}
        onChange={(next) => patch({ children: next.map((c, i) => ({ ...c, position: i + 1 })) })}
        onRemove={(i) => patch({ children: children.filter((_, idx) => idx !== i) })}
        addLabel="Add follow-up question"
        onAdd={() =>
          patch({
            children: [
              ...children,
              { type: "short_text", text: "", position: children.length + 1, points: 1, required: true, settings: {}, options: [], match_pairs: [], order_items: [], blanks: [], children: [] },
            ],
          })
        }
        renderItem={(child, i) => (
          <div className="grid gap-2">
            <select
              className={inputClass}
              value={child.type}
              onChange={(e) => patch({ children: children.map((c, idx) => (idx === i ? { ...c, type: e.target.value as QuestionType } : c)) })}
            >
              {QUESTION_TYPES.filter((t) => t !== "scenario" && t !== "section").map((t) => (
                <option key={t} value={t}>
                  {QUESTION_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <input
              className={inputClass}
              placeholder="Follow-up question text"
              value={child.text}
              onChange={(e) => patch({ children: children.map((c, idx) => (idx === i ? { ...c, text: e.target.value } : c)) })}
            />
          </div>
        )}
      />
    </div>
  );
}
